import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { captureMessage } from '@sentry/react';
import { IconArchive, IconArchiveOff, IconTrash } from '@tabler/icons-react';
import { createColumnHelper } from '@tanstack/react-table';
import { i18nKeys, useTranslation } from 'locales';
import Amount from 'shared/components/Amount';
import { useGetConstants } from 'shared/utils/selectors';
import type { LightProduct } from '../api';

const columnHelper = createColumnHelper<LightProduct>();

type ProductColumnsDeps = {
  onDelete: (product: LightProduct) => void;
  onToggleArchive: (product: LightProduct) => void;
};

const ProductActions = ({
  product,
  onDelete,
  onToggleArchive,
}: {
  product: LightProduct;
  onDelete: (product: LightProduct) => void;
  onToggleArchive: (product: LightProduct) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Group onClick={e => e.stopPropagation()} gap={4}>
      <Tooltip
        label={
          product.isArchived
            ? t(i18nKeys.INVOICING.PRODUCT.UNARCHIVE)
            : t(i18nKeys.INVOICING.PRODUCT.ARCHIVE)
        }
      >
        <ActionIcon variant="light" size="input-xs" onClick={() => onToggleArchive(product)}>
          {product.isArchived ? <IconArchiveOff size={16} /> : <IconArchive size={16} />}
        </ActionIcon>
      </Tooltip>
      <Tooltip label={t(i18nKeys.DELETE)}>
        <ActionIcon variant="light" size="input-xs" color="red" onClick={() => onDelete(product)}>
          <IconTrash size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};

export function useProductColumns({ onDelete, onToggleArchive }: ProductColumnsDeps) {
  const { t } = useTranslation();
  const constants = useGetConstants();

  return [
    columnHelper.accessor('id', {
      header: 'id',
    }),
    columnHelper.accessor('name', {
      header: t(i18nKeys.INVOICE.PRODUCT),
    }),
    columnHelper.accessor('description', {
      header: t(i18nKeys.DESCRIPTION),
    }),
    columnHelper.accessor('priceHtva', {
      header: t(i18nKeys.TOTAL_NOT_VAT_PRICE),
      cell: info => <Amount value={info.row.original.priceHtva} />,
    }),
    columnHelper.display({
      id: 'vatRate',
      header: t(i18nKeys.INVOICE.VAT_RATE),
      cell: info => {
        const product = info.row.original;
        const taxCategory = constants.tax_categories.find(
          tc => tc.value === product.taxCategoryCode,
        );

        if (taxCategory == null) {
          captureMessage('In ProductsIndex, taxCategory is null', {
            extra: { product, constants },
          });
        }

        return taxCategory?.description ?? '';
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: t(i18nKeys.FORM.ACTIONS),
      cell: info => (
        <ProductActions
          product={info.row.original}
          onDelete={onDelete}
          onToggleArchive={onToggleArchive}
        />
      ),
    }),
  ];
}
